import { supabase } from "@/lib/supabase";
import { jobColumnFor } from "@/lib/jobIdentifier";
import type { JobMessage, JobMessageStatus, MessagePriority, SenderRole, MessageCategory } from "@/data/jobMessageTypes";

// ── DB row shape — matches the actual Supabase job_messages table ─────────
export interface JobMessageRow {
  id: string;
  job_id: string | null;
  body: string;
  category: string;  // 'orders' | 'concerns' | 'general'
  sender_id: string;
  sender_name: string;
  sender_role: string;
  timestamp: string;
  priority: string;
  recipient_ids: string[];
  recipient_statuses: Record<string, JobMessageStatus>;
  mention_user_ids: string[] | null;
  context: string | null;
  related_event_id: string | null;
  created_at: string;
  updated_at: string;
}

export function toUiJobMessage(row: JobMessageRow, jobNumberByUuid: Record<string, string> = {}): JobMessage {
  return {
    id: row.id,
    // Resolve UUID → job_number so jobId matches Job.id on the frontend
    jobId: (row.job_id ? jobNumberByUuid[row.job_id] : undefined) ?? row.job_id ?? "",
    body: row.body,
    category: (row.category as MessageCategory) ?? "general",
    senderId: row.sender_id,
    senderName: row.sender_name,
    senderRole: row.sender_role as SenderRole,
    timestamp: row.timestamp,
    priority: row.priority as MessagePriority,
    recipientIds: row.recipient_ids ?? [],
    recipientStatuses: row.recipient_statuses ?? {},
    mentionUserIds: row.mention_user_ids ?? undefined,
    context: row.context ?? undefined,
    relatedEventId: row.related_event_id ?? undefined,
  };
}

export const JobMessageService = {
  // Resolve job_number → UUID, then fetch all messages for that job
  async getByJobNumber(jobNumber: string): Promise<{ data: JobMessage[] | null; error: unknown }> {
    const { data: jobRow } = await supabase
      .from("jobs")
      .select("id")
      .eq(jobColumnFor(jobNumber), jobNumber)
      .single();

    if (!jobRow) return { data: [], error: null };

    const { data, error } = await supabase
      .from("job_messages")
      .select("*")
      .eq("job_id", jobRow.id)
      .order("timestamp", { ascending: true });

    if (error || !data) return { data: null, error };
    return { data: (data as JobMessageRow[]).map(toUiJobMessage), error: null };
  },

  async getAll(limit = 500): Promise<{ data: JobMessage[] | null; error: unknown }> {
    const { data, error } = await supabase
      .from("job_messages")
      .select("*")
      .order("timestamp", { ascending: false })
      .limit(limit);

    if (error || !data) return { data: null, error };

    // Collect distinct job UUIDs and resolve them to job_number strings
    const rows = data as JobMessageRow[];
    const uuids = [...new Set(rows.map((r) => r.job_id).filter(Boolean))] as string[];
    const jobNumberByUuid: Record<string, string> = {};
    if (uuids.length > 0) {
      const { data: jobRows } = await supabase
        .from("jobs")
        .select("id, job_number")
        .in("id", uuids);
      if (jobRows) {
        for (const j of jobRows as { id: string; job_number: string | null }[]) {
          if (j.job_number) jobNumberByUuid[j.id] = j.job_number;
        }
      }
    }

    return { data: rows.map((r) => toUiJobMessage(r, jobNumberByUuid)), error: null };
  },

  // Fetch messages for a specific job UUID and category (used by realtime hook + chat UI)
  async getByJobAndCategory(
    jobId: string,
    category: MessageCategory = "general",
    limit = 200,
  ): Promise<{ data: JobMessage[] | null; error: unknown }> {
    const { data, error } = await supabase
      .from("job_messages")
      .select("*")
      .eq("job_id", jobId)
      .eq("category", category)
      .order("timestamp", { ascending: true })
      .limit(limit);

    if (error || !data) return { data: null, error };
    return { data: (data as JobMessageRow[]).map(toUiJobMessage), error: null };
  },

  // Fetch all messages for a job UUID across all categories
  async getByJobId(jobId: string, limit = 500): Promise<{ data: JobMessage[] | null; error: unknown }> {
    const { data, error } = await supabase
      .from("job_messages")
      .select("*")
      .eq("job_id", jobId)
      .order("timestamp", { ascending: true })
      .limit(limit);

    if (error || !data) return { data: null, error };
    return { data: (data as JobMessageRow[]).map(toUiJobMessage), error: null };
  },

  // Resolve the display identifier, then delegate the protected write to the Edge runtime.
  async create(msg: JobMessage, jobNumber: string): Promise<{ data: JobMessage | null; error: unknown }> {
    const { data: jobRow } = await supabase
      .from("jobs")
      .select("id")
      .eq(jobColumnFor(jobNumber), jobNumber)
      .single();

    if (!jobRow) return { data: null, error: new Error(`Job not found: ${jobNumber}`) };

    const { data: response, error } = await supabase.functions.invoke("job-message-send", {
      body: {
        jobId: jobRow.id,
        bodyHtml: msg.body,
        category: msg.category ?? "general",
        mentionedUserIds: msg.mentionUserIds ?? [],
        recipientIds: msg.recipientIds ?? [],
        priority: msg.priority,
        context: msg.context ?? null,
        relatedEventId: msg.relatedEventId ?? null,
      },
    });

    const row = (response as { message?: JobMessageRow } | null)?.message;
    if (error || !row) {
      console.error("[JobMessageService.create] error:", JSON.stringify(error));
      return { data: null, error: error ?? new Error("Invalid job-message-send response") };
    }

    return { data: toUiJobMessage(row), error: null };
  },

  // Retained for compatibility with older callers. New sends process mentions atomically
  // through job-message-send and must not invoke this a second time.
  async processMentions(messageId: string, jobId: string, senderId: string, senderName: string, mentionedUserIds: string[], category: MessageCategory, bodyPreview: string): Promise<{ data: unknown; error: unknown }> {
    if (!mentionedUserIds || mentionedUserIds.length === 0) {
      return { data: null, error: null };
    }

    const { data, error } = await supabase.functions.invoke("mention-processor", {
      body: {
        messageId,
        jobId,
        senderId,
        senderName,
        mentionedUserIds,
        category,
        bodyPreview,
      },
    });

    return { data, error };
  },

  // Update the body HTML of an existing message (used for inline invoice image deletion)
  async updateBody(messageId: string, newBody: string): Promise<{ data: JobMessage | null; error: unknown }> {
    const { data, error } = await supabase
      .from("job_messages")
      .update({ body: newBody, updated_at: new Date().toISOString() })
      .eq("id", messageId)
      .select()
      .single();

    if (error || !data) {
      console.error("[JobMessageService.updateBody] error:", JSON.stringify(error));
      return { data: null, error };
    }
    return { data: toUiJobMessage(data as JobMessageRow), error: null };
  },
};
